import {ArrayAttribute} from '../ArrayAttribute';
import type {FkBaseAttributeOptions} from './FkBaseAttribute';

export type FkArrayBaseAttributeOptions = FkBaseAttributeOptions & {
  /** Key on each embedded row for the nested entity (defaults: apiRoute, fkName, else the row if it has `id`). */
  nestedEntityKey?: string | null;
};

function isFkModelLoader(value: unknown): value is () => any {
  return (
    typeof value === 'function' &&
    Boolean((value as {__isFkModelLoader?: boolean}).__isFkModelLoader)
  );
}

/** PostgREST embedded collections (1:N / M:N link tables). Mirrors {@link FkBaseAttribute} select/embed logic; extends {@link ArrayAttribute} for [] initial value. */
export class FkArrayBaseAttribute extends ArrayAttribute {
  private _dataObjectClass: any | null = null;
  private _dataObjectClassLoader: (() => any) | null = null;
  private _listObjectClass: any | null = null;
  private _listObjectClassLoader: (() => any) | null = null;
  fkToUpdate?: string | null;
  joinPath: unknown[];
  onlyFetchDesignation?: boolean | null;
  supplementaryJoinColumns: unknown[];
  filters?: Record<string, unknown> | null;
  nestedEntityKey?: string | null;

  constructor(
    options: FkArrayBaseAttributeOptions = {},
    dataObjectClass: any | null = null,
    listObjectClass: any | null = null,
  ) {
    const {
      fkToUpdate = null,
      joinPath = [],
      onlyFetchDesignation = false,
      supplementaryJoinColumns = [],
      filters = null,
      nestedEntityKey = null,
      ...baseOptions
    } = options;

    super(baseOptions);
    this.dataObjectClass = dataObjectClass ?? null;
    this.listObjectClass = listObjectClass ?? null;
    this.fkToUpdate = fkToUpdate;
    this.joinPath = joinPath ?? [];
    this.onlyFetchDesignation = onlyFetchDesignation;
    this.supplementaryJoinColumns = supplementaryJoinColumns ?? [];
    this.filters = filters ?? null;
    this.nestedEntityKey = nestedEntityKey ?? null;
  }

  get dataObjectClass(): any | null {
    if (this._dataObjectClassLoader) {
      return this._dataObjectClassLoader();
    }
    return this._dataObjectClass;
  }

  set dataObjectClass(value: any | null) {
    if (isFkModelLoader(value)) {
      this._dataObjectClassLoader = value;
      this._dataObjectClass = null;
    } else {
      this._dataObjectClassLoader = null;
      this._dataObjectClass = value;
    }
  }

  get listObjectClass(): any | null {
    if (this._listObjectClassLoader) {
      return this._listObjectClassLoader();
    }
    return this._listObjectClass;
  }

  set listObjectClass(value: any | null) {
    if (isFkModelLoader(value)) {
      this._listObjectClassLoader = value;
      this._listObjectClass = null;
    } else {
      this._listObjectClassLoader = null;
      this._listObjectClass = value;
    }
  }

  get inputType(): string {
    return 'fk_entity_array';
  }

  get customTableProperties(): Object {
    return {
      dataObjectClass: this.dataObjectClass,
      customFilters: this.filters,
    };
  }

  get dbColumn(): string {
    if (this.fkToUpdate) return this.fkToUpdate;
    const fkName =
      this.dataObjectClass == null
        ? undefined
        : (this.dataObjectClass as unknown as {fkName?: string}).fkName;
    return fkName ?? this.column ?? this.name;
  }

  get selectString(): string {
    if (!this.dataObjectClass || !this.joinPath?.length) {
      return super.selectString;
    }

    let str = '';
    this.joinPath.forEach((path) => {
      str += path + '(';
    });
    str += this.dataObjectClass.entityColumns(
      this.onlyFetchDesignation
        ? this.dataObjectClass.joinAttrsForDesignation.concat(
            this.dataObjectClass.getAttributes(this.supplementaryJoinColumns),
          )
        : this.dataObjectClass.getAttributes(
            this.dataObjectClass.joinAttrsKey.concat(
              this.supplementaryJoinColumns,
            ),
          ),
    );
    str += ')'.repeat(this.joinPath.length);
    return `${this.name}:${str}`;
  }

  /**
   * PostgREST select fragment: a named inner embed on the link table used only for
   * filtering (same idea as qualification `*_filter:…!inner(…)`).
   */
  get filterEmbedSelectString(): string {
    if (!this.joinPath?.length) {
      return '';
    }
    const first = String(this.joinPath[0]);
    const linkTable = first.split('!')[0];
    if (!linkTable) {
      return '';
    }
    const fk =
      this.fkToUpdate ??
      (this.dataObjectClass as {fkName?: string} | null | undefined)?.fkName ??
      'id';
    return `${this.name}_filter:${linkTable}!inner(${fk})`;
  }

  protected resolveNestedEntityPayload(
    row: Record<string, unknown>,
  ): Record<string, unknown> | null {
    if (this.nestedEntityKey && row[this.nestedEntityKey] != null) {
      const v = row[this.nestedEntityKey];
      if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
        return v as Record<string, unknown>;
      }
    }
    const cls = this.dataObjectClass as
      | {apiRoute?: string; fkName?: string}
      | null
      | undefined;
    const keys = [cls?.apiRoute, cls?.fkName].filter(Boolean) as string[];
    for (const key of keys) {
      const v = row[key];
      if (v != null && typeof v === 'object' && !Array.isArray(v)) {
        return v as Record<string, unknown>;
      }
    }
    if ('id' in row && row.id != null) {
      return row;
    }
    return null;
  }

  toLocalValue(item: Record<string, unknown>): unknown {
    const value = item[this.name];
    if (!value) return [];
    if (!Array.isArray(value)) return [];

    const EntityClass = this.dataObjectClass;
    if (!EntityClass) {
      const rawResult = this.transform
        ? value.map((v) => this.transform!(v))
        : value;
      if (!this.listObjectClass) return rawResult;
      return new this.listObjectClass(rawResult);
    }

    const result: unknown[] = [];
    for (const raw of value) {
      if (typeof raw !== 'object' || raw === null) continue;
      let payload = this.resolveNestedEntityPayload(
        raw as Record<string, unknown>,
      );
      if (!payload) continue;
      if (this.transform) {
        payload = this.transform(payload) as Record<string, unknown>;
      }
      if (typeof payload === 'object' && payload !== null && 'id' in payload) {
        result.push(new EntityClass(payload));
      }
    }
    if (!this.listObjectClass) return result;
    return new this.listObjectClass(result);
  }
}
